import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, ChevronDown, ChevronRight, Globe2, Search } from 'lucide-react';
import { api, type EligibleGeoclient, type GeoclientFilterClient, type GeoclientFilterMarket } from '../lib/api';

type ClientGroupBucket = {
  clientId: string;
  clientName: string;
  markets: EligibleGeoclient[];
};

function nestGroupsByClient(groups: EligibleGeoclient[]): ClientGroupBucket[] {
  const byClient = new Map<string, ClientGroupBucket>();
  for (const g of groups) {
    let bucket = byClient.get(g.clientId);
    if (!bucket) {
      bucket = {
        clientId: g.clientId,
        clientName: g.clientName,
        markets: [],
      };
      byClient.set(g.clientId, bucket);
    }
    bucket.markets.push(g);
  }

  const buckets = [...byClient.values()];
  buckets.sort((a, b) => a.clientName.localeCompare(b.clientName));
  for (const bucket of buckets) {
    bucket.markets.sort(
      (a, b) => a.marketName.localeCompare(b.marketName) || a.code.localeCompare(b.code),
    );
  }
  return buckets;
}

export default function GroupsList() {
  const [groups, setGroups] = useState<EligibleGeoclient[]>([]);
  const [markets, setMarkets] = useState<GeoclientFilterMarket[]>([]);
  const [clients, setClients] = useState<GeoclientFilterClient[]>([]);
  const [marketCode, setMarketCode] = useState('');
  const [clientCode, setClientCode] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedClientIds, setExpandedClientIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    Promise.all([api.listGeoclientFilterMarkets(), api.listGeoclientFilterClients()])
      .then(([m, c]) => {
        setMarkets(m);
        setClients(c);
      })
      .catch(() => {
        setMarkets([]);
        setClients([]);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listGeoclients({
        marketCode: marketCode || undefined,
        clientCode: clientCode || undefined,
        q: debouncedQ || undefined,
      })
      .then((rows) => {
        if (!cancelled) setGroups(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          setGroups([]);
          setError((err as Error).message || 'Failed to load geoclients');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [marketCode, clientCode, debouncedQ]);

  const clientBuckets = useMemo(() => nestGroupsByClient(groups), [groups]);

  useEffect(() => {
    setExpandedClientIds(new Set());
  }, [marketCode, clientCode]);

  useEffect(() => {
    if (!debouncedQ) {
      setExpandedClientIds(new Set());
      return;
    }
    setExpandedClientIds(new Set(clientBuckets.map((b) => b.clientId)));
  }, [debouncedQ, clientBuckets]);

  const emptyLabel = useMemo(() => {
    if (marketCode || clientCode || debouncedQ) return 'No geoclients match your filters.';
    return 'No geoclients are assigned to your account yet.';
  }, [marketCode, clientCode, debouncedQ]);

  const toggleClient = (clientId: string) => {
    setExpandedClientIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const clientCountLabel =
    clientBuckets.length === 1 ? '1 client' : `${clientBuckets.length} clients`;
  const groupCountLabel =
    groups.length === 1 ? '1 geoclient' : `${groups.length} geoclients`;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Market</span>
          <select
            className="input mt-1"
            value={marketCode}
            onChange={(e) => setMarketCode(e.target.value)}
          >
            <option value="">All markets</option>
            {markets.map((m) => (
              <option key={m.code} value={m.code}>
                {m.name} ({m.regionCode})
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Client</span>
          <select
            className="input mt-1"
            value={clientCode}
            onChange={(e) => setClientCode(e.target.value)}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Search</span>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              placeholder="Type a geoclient or client name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </label>
      </div>

      <div className="card overflow-hidden">
        <div className="card-header">
          <h2 className="text-base font-semibold">Geoclients</h2>
          <span className="text-xs text-slate-500">
            {loading ? 'Loading…' : `${clientCountLabel} · ${groupCountLabel}`}
          </span>
        </div>
        <div className="divide-y divide-slate-100">
          {error ? (
            <div className="px-5 py-8 text-sm text-rose-600">{error}</div>
          ) : loading ? (
            <div className="px-5 py-8 text-sm text-slate-500">Loading geoclients…</div>
          ) : clientBuckets.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <Building2 className="mx-auto text-slate-300 mb-3" size={28} />
              <div className="text-sm font-medium text-slate-700">{emptyLabel}</div>
              <p className="text-xs text-slate-500 mt-1">
                Geoclient access is managed in GIA Admin.
              </p>
            </div>
          ) : (
            clientBuckets.map((bucket) => {
              const expanded = expandedClientIds.has(bucket.clientId);
              const marketCount =
                bucket.markets.length === 1
                  ? '1 market'
                  : `${bucket.markets.length} markets`;
              return (
                <div key={bucket.clientId}>
                  <button
                    type="button"
                    onClick={() => toggleClient(bucket.clientId)}
                    aria-expanded={expanded}
                    className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="text-slate-400 shrink-0">
                      {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">{bucket.clientName}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{marketCount}</div>
                    </div>
                  </button>
                  {expanded && (
                    <div className="border-t border-slate-50 bg-slate-50/60">
                      {bucket.markets.map((g) => (
                        <Link
                          key={g.id}
                          to={`/clients/${g.clientId}?geoclientId=${encodeURIComponent(g.id)}`}
                          className="flex items-center gap-3 px-5 py-3 pl-12 hover:bg-white transition-colors group"
                        >
                          <Globe2 size={16} className="text-slate-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-800 truncate">
                              {g.name}
                            </div>
                            <div className="text-xs text-slate-500">{g.marketCode}</div>
                          </div>
                          <ArrowRight
                            size={16}
                            className="text-slate-300 group-hover:text-slate-500 shrink-0"
                          />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
